import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useSkillCategory custom hook for skill category operations.
 * - Provides CRUD operations for skill categories using Apollo Client and GraphQL.
 * - Handles cache updates for skill category queries and mutations.
 * - Exposes helpers for reading/writing/updating skill categories in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting skill categories.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const SKILL_CATEGORY_QUERIES = {
    // Query to fetch all skill categories
    GET_SKILL_CATEGORY: gql`
    query GetSkillCategories {
      GetSkillCategories {
        skill_category_id
        skill_cat_name
        is_active
        created_on
        updated_on
      }
    }
  `,

    // Mutation to add a new skill category
    ADD_SKILL_CATEGORY: gql`
    mutation AddSkillCategory(
      $skill_cat_name: String!
    ) {
      AddSkillCategory(
        skill_cat_name:$skill_cat_name
      ) {
       skill_category_id
       skill_cat_name
        is_active
        created_on
        updated_on
      }
    }
  `,

    // Mutation to update an existing skill category
    UPDATE_SKILL_CATEGORY: gql`
      mutation UpdateSkillCategory(
        $skill_category_id: ID!
        $skill_cat_name: String
        $is_active: Boolean
      ) {
        UpdateSkillCategory(
          skill_category_id: $skill_category_id
          skill_cat_name: $skill_cat_name
          is_active:$is_active
        ) {
          skill_cat_name
          skill_category_id
          is_active
          created_on
          updated_on
        }
      }
    `,

    // Mutation to delete a skill category
    DELETE_SKILL_CATEGORY: gql`
    mutation DeleteSkillCategory($skill_category_id: ID!) {
      DeleteSkillCategory(skill_category_id: $skill_category_id) {
        skill_category_id
      }
    }
  `,
};

// Cache operations
const cacheOperations = {
    /**
     * Reads all skill categories from Apollo cache.
     * @returns {Array} Array of skill category objects.
     */
    readSkillCategory: () => {
        try {
            const { GetSkillCategories = [] } =
                client.readQuery({ query: SKILL_CATEGORY_QUERIES.GET_SKILL_CATEGORY }) || {};
            return GetSkillCategories;
        } catch {
            return [];
        }
    },
    /**
     * Writes skill categories to Apollo cache.
     * @param {Array} categories
     */
    writeSkillCategory: (categories) => {
        client.writeQuery({
            query: SKILL_CATEGORY_QUERIES.GET_SKILL_CATEGORY,
            data: { GetSkillCategories: categories },
        });
    },
};

export const useSkillCategory = () => {
    // Add skill category mutation with cache update
    const [addSkillCategory] = useMutation(SKILL_CATEGORY_QUERIES.ADD_SKILL_CATEGORY, {
        update(cache, { data: { AddSkillCategory } }) {
            try {
                const existingData = cache.readQuery({
                    query: SKILL_CATEGORY_QUERIES.GET_SKILL_CATEGORY,
                });
                if (existingData) {
                    cache.writeQuery({
                        query: SKILL_CATEGORY_QUERIES.GET_SKILL_CATEGORY,
                        data: {
                            GetSkillCategories: [AddSkillCategory, ...existingData.GetSkillCategories],
                        },
                    });
                }
            } catch (error) {
                console.error("Cache update error:", error);
            }
        },
    });

    // Update skill category mutation with cache update
    const [updateSkillCategory] = useMutation(SKILL_CATEGORY_QUERIES.UPDATE_SKILL_CATEGORY, {
        update(cache, { data: { UpdateSkillCategory } }) {
            try {
                const existingData = cache.readQuery({
                    query: SKILL_CATEGORY_QUERIES.GET_SKILL_CATEGORY,
                });
                if (existingData) {
                    const newData = existingData.GetSkillCategories.map((category) =>
                        category.skill_category_id === UpdateSkillCategory.skill_category_id
                            ? UpdateSkillCategory
                            : category
                    );
                    cache.writeQuery({
                        query: SKILL_CATEGORY_QUERIES.GET_SKILL_CATEGORY,
                        data: { GetSkillCategories: newData },
                    });
                }
            } catch (error) {
                console.error("Error updating cache after updating skill category:", error);
            }
        },
    });

    // Delete skill category mutation with cache update
    const [deleteSkillCategory] = useMutation(SKILL_CATEGORY_QUERIES.DELETE_SKILL_CATEGORY, {
        update(cache, { data: { DeleteSkillCategory } }) {
            try {
                const existingData = cache.readQuery({
                    query: SKILL_CATEGORY_QUERIES.GET_SKILL_CATEGORY,
                });
                if (existingData) {
                    cache.writeQuery({
                        query: SKILL_CATEGORY_QUERIES.GET_SKILL_CATEGORY,
                        data: {
                            GetSkillCategories: existingData.GetSkillCategories.filter(
                                (category) => category.skill_category_id !== DeleteSkillCategory.skill_category_id
                            ),
                        },
                    });
                }
            } catch (error) {
                console.error("Error updating cache after deleting skill category:", error);
            }
        },
    });

    /**
     * Fetches all skill categories from the backend.
     * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
     */
    const GetSkillCategories = async () => {
        try {
            const { data } = await client.query({
                query: SKILL_CATEGORY_QUERIES.GET_SKILL_CATEGORY,
                fetchPolicy: "network-only",
                nextFetchPolicy: "cache-first",
            });

            if (!data?.GetSkillCategories) {
                throw new Error("No categories data received");
            }

            return { success: true, data: data.GetSkillCategories };
        } catch (error) {
            console.error("Category fetch error:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Creates a new skill category.
     * @param {Object} formData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const createSkillCategory = async (formData) => {
        try {
            const { data } = await addSkillCategory({
                variables: {
                    skill_cat_name: formData.category_name,
                },
            });

            if (data?.AddSkillCategory) {
                return { success: true, data: data.AddSkillCategory };
            } else {
                throw new Error("Failed to create skill category");
            }
        } catch (error) {
            console.error("Error creating skill category:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Updates an existing skill category.
     * @param {string|number} category_id
     * @param {Object} formData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const UpdateSkillCategory = async (category_id, formData) => {
        try {
            const { data } = await updateSkillCategory({
                variables: {
                    skill_category_id: parseInt(category_id),
                    skill_cat_name: formData.category_name,
                    // is_active: formData.is_active
                },
            });

            if (data?.UpdateSkillCategory) {
                return { success: true, data: data.UpdateSkillCategory };
            } else {
                throw new Error("Failed to update skill category");
            }
        } catch (error) {
            console.error("Error updating skill category:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Deletes a skill category by its ID.
     * @param {string|number} categoryId
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const DeleteSkillCategory = async (categoryId) => {
        try {
            const { data } = await deleteSkillCategory({
                variables: { skill_category_id: parseInt(categoryId) },
            });

            return { success: true, data: data.DeleteSkillCategory };
        } catch (error) {
            console.error("Error deleting skill category:", error);
            return { success: false, error: error.message };
        }
    };

    return {
        /**
         * Fetch all skill categories.
         * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
         */
        GetSkillCategories,
        /**
         * Create a new skill category.
         * @param {Object} formData
         * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
         */
        createSkillCategory,
        /**
         * Update an existing skill category.
         * @param {string|number} category_id
         * @param {Object} formData
         * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
         */
        UpdateSkillCategory,
        /**
         * Delete a skill category by its ID.
         * @param {string|number} categoryId
         * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
         */
        DeleteSkillCategory,
        /**
         * Cache operations for skill categories.
         */
        cacheOperations,
    };
};
